/**
 * Corner-anchored tour card — non-modal, non-blocking.
 *
 * Previously rendered a full-height right-side slideout behind a
 * `bg-ink/30 backdrop-blur-[2px]` overlay that covered the entire
 * viewport. That overlay is now gone. The card floats in the
 * bottom-right corner at a lower z-index so users can interact
 * freely with the page while the hint sits in view.
 *
 * Mobile: same card, safe-area aware via pb-safe. Max-width is
 * constrained so it never full-screens on narrow viewports.
 *
 * Esc still dismisses.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useOnboarding } from './OnboardingContext';
import type { StepDef } from './types';

export function TourSlideout({ step }: { step: StepDef }): JSX.Element {
  const { currentIndex, totalSteps, goNext, goPrev, dismiss } = useOnboarding();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dismiss]);

  const isLast = currentIndex === totalSteps - 1;

  return createPortal(
    /* No backdrop — the card is non-blocking and non-modal. */
    <div
      role="complementary"
      aria-label={step.title}
      data-testid="tour-slideout-card"
      className={cn(
        // Corner anchor: bottom-right, above BottomTabs (pb-[72px] on mobile)
        // but below dialogs (z-[9000] keeps it out of Prumo/Housy territory).
        'fixed bottom-[72px] right-4 z-[9000] w-72 max-w-[calc(100vw-2rem)]',
        'flex flex-col rounded-xl border border-line bg-surface shadow-elev-3',
        'animate-[fadeSlideUp_220ms_ease-out]',
        // Safe-area padding for notched phones when BottomTabs isn't present.
        'sm:bottom-6',
      )}
    >
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
          {currentIndex + 1} / {totalSteps}
        </span>
        <button
          type="button" onClick={dismiss}
          className="p-1 text-sm text-ink-subtle hover:text-ink"
          aria-label="Fechar tour"
        >✕</button>
      </div>
      <div className="mb-3 px-4">
        <div className="h-1 overflow-hidden rounded-full bg-line">
          <div
            className="h-full rounded-full bg-brand-400 transition-[width] duration-500"
            style={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>
      <div className="overflow-y-auto px-4 pb-1">
        <h2 className="mb-2 text-sm font-bold text-ink">{step.title}</h2>
        <p className="text-xs leading-relaxed text-ink-muted">{step.body}</p>
      </div>
      <div className="flex items-center gap-2 border-t border-line px-4 py-3">
        {currentIndex > 0 && !isLast && (
          <button type="button" onClick={goPrev} className="text-xs text-ink-muted hover:text-ink">
            ← Voltar
          </button>
        )}
        <div className="flex-1" />
        <Button variant="primary" onClick={isLast ? dismiss : goNext}>
          {step.ctaLabel ?? (isLast ? 'Concluir' : 'Próximo →')}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
